import React, { memo, useCallback, useEffect, useMemo } from "react";

import {
  MessageFragment,
  MessageListQuery,
  MessageListQueryVariables,
  MessageThreadFragment,
  MessageType,
} from "gql/graphql";
import { Virtuoso } from "react-virtuoso";
import { useQuery } from "@apollo/client";
import MESSAGE_LIST_QUERY from "modules/messaging/_networking/message-list";
import {
  Box,
  Button,
  Center,
  DrawerBody,
  DrawerFooter,
  HStack,
  Spinner,
  useCheckboxGroup,
  Text,
  Flex,
} from "@chakra-ui/react";
import ForwardToMessage from "./forward-to-message";
import colors from "app/colors";
import useMessageThreadDraft from "modules/hooks/use-message-thread-draft";
import { CloseIcon } from "modules/icons";
import { canForwardMessage } from "modules/utils/Functions";

interface IProps {
  draftId: string;
  messageThread: MessageThreadFragment;
  onNextClicked: (messages?: MessageFragment[]) => void;
  nextButtonLabel?: string;
  isNextLoading?: boolean;
  disableNextIfNoneSelected?: boolean;
}

const ForwardToSelectMessages = ({
  draftId,
  messageThread,
  onNextClicked,
  nextButtonLabel = "Next",
  isNextLoading = false,
  disableNextIfNoneSelected = false,
}: IProps) => {
  const { draft, setQuotedMessages, copyFiles } = useMessageThreadDraft({
    draftId,
  });

  const { data, loading } = useQuery<
    MessageListQuery,
    MessageListQueryVariables
  >(MESSAGE_LIST_QUERY, {
    variables: {
      messageThreadId: messageThread.id,
    },
  });

  //only user messages can be forwarded
  const messageList = useMemo(
    () =>
      data?.messageList?.filter(
        (message) => message.type !== MessageType.System,
      ) || [],
    [data],
  );

  const quotedMessageIds = useMemo(() => {
    if (!draft?.quotedMessageList) return [];
    return draft.quotedMessageList.map((message) => message.messageId);
  }, [draft?.quotedMessageList]);

  const scrollToMessageId = useMemo(() => {
    if (!quotedMessageIds || quotedMessageIds.length === 0) {
      return undefined;
    }
    return quotedMessageIds[0];
  }, [quotedMessageIds]);

  const { getCheckboxProps, value, setValue } = useCheckboxGroup();

  useEffect(() => {
    setValue(quotedMessageIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quotedMessageIds]);

  const _onNextClicked = useCallback(() => {
    if (isNextLoading) {
      return;
    }
    const messages = messageList.filter((message) =>
      value.includes(message.id),
    );
    if (disableNextIfNoneSelected && messages.length === 0) {
      return;
    }
    setQuotedMessages(...messages);
    const emailMessage = messages.find(
      (message) => message.type === MessageType.Email,
    );
    if (emailMessage && emailMessage.htmlPdfFile) {
      copyFiles({ ...emailMessage.htmlPdfFile });
    }
    copyFiles(...messages.map((message) => message.fileList).flat());
    onNextClicked(messages);
  }, [
    copyFiles,
    disableNextIfNoneSelected,
    isNextLoading,
    messageList,
    onNextClicked,
    setQuotedMessages,
    value,
  ]);

  const onClearClicked = useCallback(() => {
    setValue([]);
  }, [setValue]);

  const onSelectAllClicked = useCallback(() => {
    setValue(
      messageList
        .filter((message) => canForwardMessage(message))
        .map((message) => message.id),
    );
  }, [messageList, setValue]);

  const initialTopMostItemIndex = useMemo(() => {
    if (scrollToMessageId) {
      return messageList.findIndex(
        (message) => message.id === scrollToMessageId,
      );
    }

    return messageList.length - 1;
  }, [messageList, scrollToMessageId]);

  const renderMessage = useCallback(
    (_: number, message: MessageFragment) => (
      <ForwardToMessage
        messageThread={messageThread}
        message={message}
        checkboxProps={getCheckboxProps({ value: message.id })}
      />
    ),
    [getCheckboxProps, messageThread],
  );

  if (loading || !messageList.length || initialTopMostItemIndex === undefined) {
    return (
      <Center h="100%">
        <Spinner />
      </Center>
    );
  }
  return (
    <>
      <DrawerBody p={0}>
        <Flex direction="column" h="full">
          <HStack
            py={4}
            px={4}
            w="full"
            justifyContent="space-between"
            flexShrink={0}
          >
            <Button
              variant="link"
              color={colors.blue}
              fontSize={14}
              fontWeight={600}
              onClick={onSelectAllClicked}
            >
              Select All
            </Button>
            {value.length && (
              <Text
                fontSize={14}
                fontWeight={600}
                color={colors.blackSecondary}
              >{`${value.length} Selected`}</Text>
            )}
            <Button
              fontWeight={600}
              fontSize={14}
              variant="ghost"
              size="xs"
              leftIcon={<CloseIcon fill={colors.gray40} boxSize={4} />}
              color={colors.gray40}
              onClick={onClearClicked}
              iconSpacing={1}
            >
              Clear
            </Button>
          </HStack>
          <Virtuoso
            initialTopMostItemIndex={initialTopMostItemIndex}
            data={messageList}
            itemContent={renderMessage}
          />
        </Flex>
      </DrawerBody>
      <DrawerFooter p={0} px={3} mb={3}>
        <Box
          w="full"
          textAlign="center"
          borderTopColor={colors.grayPrimary}
          borderTopWidth={1}
          pt={4}
        >
          <Button
            w="380px"
            onClick={_onNextClicked}
            isLoading={isNextLoading}
            isDisabled={
              isNextLoading || (disableNextIfNoneSelected && !value.length)
            }
          >
            {nextButtonLabel}
          </Button>
        </Box>
      </DrawerFooter>
    </>
  );
};

export default memo(ForwardToSelectMessages);
